import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDataProvider } from 'react-admin';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SnackbarContainer } from 'components/standalone';
import { extractErrorMessage } from 'utils';
import { SupplierContainer, SupplierFormValues } from '../containers';
import { SupplierForm } from '../components';

const SuppliersCreateInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const dataProvider = useDataProvider();
  const { notify } = SnackbarContainer.useContainer();
  const {
    formLoading,
    createSupplier,
    loadRegionOptions,
    loadUserRepresentativeOptions,
    setFormLoading,
    regionsError,
  } = SupplierContainer.useContainer();

  const handleSubmit = useCallback(
    async (values: SupplierFormValues) => {
      setFormLoading(true);
      try {
        const created = await createSupplier({
          name: values.name,
          externalId: values.externalId,
          description: values.description,
          email: values.email,
          phone: values.phone,
          contactInfo: values.contactInfo,
        });

        await Promise.all(
          values.selectedRegions.map(regionId =>
            dataProvider.create('supplier-region', {
              data: { supplierId: created.id, regionId },
            }),
          ),
        );

        notify('Element created');
        history.push(`/supplier/${created.id}`);
      } catch (error: unknown) {
        console.error('Supplier create failed:', error);
        notify(
          extractErrorMessage(error, 'Error while creating the supplier'),
          'error',
        );
      } finally {
        setFormLoading(false);
      }
    },
    [createSupplier, dataProvider, history, notify, setFormLoading],
  );

  const handleGoBack = useCallback(() => {
    history.push('/supplier');
  }, [history]);

  return (
    <Box className={classes.formContainer}>
      <SupplierForm
        mode="create"
        onSubmit={handleSubmit}
        loading={formLoading}
        onGoBack={handleGoBack}
        loadRegionOptions={loadRegionOptions}
        loadUserRepresentativeOptions={loadUserRepresentativeOptions}
        regionsError={regionsError}
      />
    </Box>
  );
};

export const SuppliersCreate = () => (
  <SupplierContainer.Provider>
    <SuppliersCreateInner />
  </SupplierContainer.Provider>
);

const useStyles = makeStyles(theme => ({
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
}));

export default SuppliersCreate;
